import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { useAuth } from "../../contexts/AuthContext";
import { isClientRole, isAdminRole } from '../../utils/roleUtils';
import { useNotifications } from "../../contexts/NotificationsContextType";
import { handleExportCSV } from '../../utils/commonHandlers';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import SearchBar from '../../composants/SearchBar/SearchBar';
import Button from '../../composants/Button/Button';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import TabNav from '../../composants/TabNav/TabNav';
import FiltreDeCommande from '../../composants/FiltreDeCommande/FiltreDeCommande';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';

import './Commandes.css';

// Interface représentant la structure des commandes renvoyées par l'API
interface Commande {
  id: string;
  id_lot_departement: number;
  id_contrat: number;
  id_user: number | null;
  type_compte: string | null;
  id_entite: number;
  id_note_technique: number | null;
  id_status: number;
  nom: string | null;
  delais: number | null;
  adresse: string | null;
  cordonnee: string | null;
  modalite: string | null;
  visite: boolean | null;
  commentaire_visite: string | null;
  note_financiere: number | null;
  note_total: number | null;
  prix_total: number | null;
  created_at: string;
  updated_at: string;
  id_operation: number;
  expire_at: string | null;
  reponse: string | null; // Champ pour stocker la réponse technique
  operation: {
    id?: number;
    organisme_id?: number;
    organisme_nom: string;
    id_user?: number;
    id_lot_technique?: number;
    id_lot_departement?: number;
    nom?: string;
    numero?: string;
    // autres champs possibles
  };
  status: {
    id: number;
    action: boolean;
    nom: string;
    delai: number | null;
    type_compte: string;
  };
  articles: any[];
  // Propriétés supplémentaires pour la présentation
  organisme_nom?: string;
  lot_technique?: {
    id: number;
    nom: string;
  };
  cree_par?: string;
}

// Interface pour la réponse de l'API
interface ApiResponse {
  status: string;
  data: Commande[];
  request_params: {
    interlocuteur_id: string;
    id_entite: string;
  };
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

function Commandes({ onNotificationUpdate }: { onNotificationUpdate?: (count: number) => void }) {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const navigate = useNavigate();

  // États
  const [isLancerMarcheModalOpen, setIsLancerMarcheModalOpen] = useState(false);
  const [isBonCommandeModalOpen, setIsBonCommandeModalOpen] = useState(false);

  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [filteredData, setFilteredData] = useState<Commande[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [unreadCommandes, setUnreadLocalCommandes] = useState<number>(0);

  // Contexte de notifications
  const { setUnreadCommandes } = useNotifications();

  // Définition des onglets - basé sur les statuts de l'API
  const tabs = [
    'Tous',
    'Action requise',
    'En attente',
    'Brouillons',
    'Acceptés',
    'Refusé / Expiré' // ✅ Fusionné
  ];
  const [activeTab, setActiveTab] = useState(1); // 'Action requise' par défaut

  // Mapping entre les onglets et les IDs de statut de l'API
  const tabToStatusMapping: { [key: number]: { filter: string, statusIds: number[] } } = {
    0: { filter: '', statusIds: [] }, // Tous
    1: { filter: 'status_action=1', statusIds: [] }, // Action requise - filtre par l'attribut action=true
    2: { filter: 'status_action=0&status_type_compte=Prestataire', statusIds: [] }, // En attente
    3: { filter: 'status_id=1', statusIds: [1] }, // Brouillons - statut 1 (Brouillon)
    4: { filter: 'status_id=11', statusIds: [11] }, // Accepté - statut 11 (Valider)
    5: { filter: 'status_id=6,12', statusIds: [6, 12] }, // ✅ Fusionné Refusé / Expiré - statuts 6 et 12
  };

  // État de pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 50,
    total: 0
  });

  // Helper pour récupérer le rôle utilisateur du localStorage
  const getUserRoleFromLocalStorage = (): string => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        return userData.role || '';
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du rôle utilisateur:', error);
    }
    return '';
  };

  // Handler pour ouvrir le modal de lancement de marché subséquent
  const handleOpenLancerMarcheModal = () => {
    setIsLancerMarcheModalOpen(true);
  };

  // Options de filtre de type
  const typeOptions = [
    { label: 'Tous', value: 'Tous' },
    { label: 'Bon de Commande', value: 'BC' },
    { label: 'Marché Subséquent', value: 'MS' }
  ];
  const [selectedType, setSelectedType] = useState<string>(typeOptions[0].value);

  // Pour le changement d'onglet
  const handleTabChange = (tabIndex: number) => {
    setActiveTab(tabIndex);
    fetchCommandes(1, tabIndex);
  };

  // Pour le changement de type
  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    fetchCommandes(1, activeTab);
  };

  // Fonction qui détermine quelle action afficher en fonction du statut et du rôle de l'utilisateur
  const getStatusActionMapping = (statusId: number, userRole: string) => {
    // Mapping complet des statuts vers leurs actions
    const statusActionMap: {
      [key: number]: {
        buttonText: string,
        action: string,
        forRoles: string[],
        requiresAction: boolean
      }
    } = {
      1: { // Brouillon
        buttonText: 'Reprendre',
        action: 'reprendre',
        forRoles: ['Admin', 'Prestataire admin'],
        requiresAction: false
      },
      2: { // Candidature en cours
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      3: { // A candidater
        buttonText: 'Candidater',
        action: 'candidater',
        forRoles: ['Prestataire', 'Prestataire admin', 'Admin'],
        requiresAction: true
      },
      4: { // En analyse
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      5: { // En attente de l'analyse
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      6: { // Refuser
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      7: { // En attente de votre signature (acheteur)
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      8: { // En attente decision
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      9: { // En attente de signature prestataire
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      10: { // En attente de votre signature (prestataire)
        buttonText: 'Signer',
        action: 'signer',
        forRoles: ['Prestataire', 'Prestataire admin', 'Admin'],
        requiresAction: true
      },
      11: { // Valider
        buttonText: 'PDF',
        action: 'export',
        forRoles: ['Prestataire', 'Prestataire admin', 'Admin'],
        requiresAction: false
      },
      12: { // Expirer
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      }
    };

    // Si le statut n'existe pas dans le mapping, retourner une configuration par défaut
    if (!statusActionMap[statusId]) {
      return {
        buttonText: '',
        action: '',
        showButton: false,
        disableButton: true
      };
    }

    const mapping = statusActionMap[statusId];

    // Déterminer si le bouton doit être affiché en fonction du rôle de l'utilisateur
    // Vérifier si le rôle de l'utilisateur (même partiel) correspond à l'un des rôles autorisés
    const showButton = mapping.buttonText !== '' &&
      (mapping.forRoles.some(role =>
        userRole.toLowerCase().includes(role.toLowerCase())) ||
        mapping.forRoles.includes('*'));

    // Déterminer si le bouton doit être activé en fonction de requiresAction
    // Si requiresAction est true, le bouton ne doit être activé que si action est true dans le statut
    const disableButton = false; // Nous laissons les boutons toujours activés pour l'instant

    return {
      buttonText: mapping.buttonText,
      action: mapping.action,
      showButton,
      disableButton
    };
  };

  // Fonctions de navigation
  const handleRowClick = (id: string) => {
    const commande = commandes.find(commande => commande.id === id);
    console.log(commande)
    if (!commande) return;

    console.log("Commande cliquée:", commande); // Débogage

    // Déterminer l'action basée sur le statut
    let action = '';
    let tab = 0;
    // Logique de redirection basée sur le statut
    if (commande.status.nom === 'Brouillon') {
      action = 'reprendre';
      tab = 0; // Tab Suivi du Marché
    } else if (commande.status.nom === 'En attente de l\'analyse' || commande.status.nom === 'En analyse') {
      action = '';
      tab = 1; // Candidature
    } else if (commande.status.nom === 'En attente de votre signature') {
      action = 'signer';
      tab = 2; // Tab Les pièces du marché
    } else if (commande.status.nom === 'À candidater') {
      action = 'candidater';
      tab = 1; // Tab Candidature pour prestataire
    } else {
      // Cas par défaut pour les statuts non traités spécifiquement
      action = 'view';
      tab = 1; // Tab par défaut
    }

    console.log(`Redirection vers /commandes/${id}?tab=${tab} avec action ${action}`); // Débogage
    navigate(`/commandes/${id}?tab=${tab}`, { state: { action } });
  };

  // Actions spécifiques
  const handleCandidater = (operationId: string) => {
    navigate(`/commandes/${operationId}?tab=1`, { state: { action: 'candidater' } });
  };

  const handleAnalyser = (operationId: string) => {
    navigate(`/commandes/${operationId}?tab=2`, { state: { action: 'analyser' } });
  };

  const handleReprendre = (operationId: string) => {
    navigate(`/commandes/${operationId}?tab=1`, { state: { action: 'reprendre' } });
  };

  const handleSigner = (operationId: string) => {
    navigate(`/commandes/${operationId}?tab=3`, { state: { action: 'signer' } });
  };

  const handleExport = (id: string) => {
    const commandeToExport = commandes.find(cmd => cmd.id.toString() === id);
    if (commandeToExport) {
      handleExportCSV([commandeToExport], columns, `commande-${id}-${new Date().toISOString().slice(0, 10)}.csv`);
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/commande/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'exportation des commandes');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export-commandes-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Une erreur est survenue lors de l\'export des commandes');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRelancerSignataire = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${id}/relancer-signataire`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la relance');
      }

      alert('Relance envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de la relance:', error);
      alert('Une erreur est survenue lors de l\'envoi de la relance');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchCommandes(page, activeTab);
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handleSearch = async (query: string) => {
    setSearchLoading(true);
    try {
      if (!query.trim()) {
        await fetchCommandes(1, activeTab);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation?operation_nom=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || 'Erreur lors de la recherche');
        throw new Error('Erreur lors de la recherche');
      }

      const data = await response.json();

      if (data.status === 'success') {
        setCommandes(data.data);
        setFilteredData(data.data);

        // Utiliser les informations de pagination de la réponse ou générer des valeurs par défaut
        if (data.pagination) {
          setPagination({
            currentPage: data.pagination.current_page,
            lastPage: data.pagination.last_page,
            perPage: data.pagination.per_page,
            total: data.pagination.total
          });
        } else {
          setPagination({
            currentPage: 1,
            lastPage: Math.ceil(data.data.length / 50),
            perPage: 50,
            total: data.data.length
          });
        }
      } else {
        setCommandes([]);
        setFilteredData([]);
        setPagination({ currentPage: 1, lastPage: 1, perPage: 50, total: 0 });
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setError('Une erreur est survenue lors de la recherche.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedItems(selectedIds);
  };

  // Appliquer les filtres à nos données
  const handleFilterApply = (filters: any) => {
    let filtered = [...commandes];

    // Appliquer les filtres selon les critères
    if (filters.dateRange.start && filters.dateRange.end) {
      filtered = filtered.filter(commande => {
        const cmdDate = new Date(commande.created_at);
        const startDate = filters.dateRange.start as Date;
        const endDate = filters.dateRange.end as Date;
        return cmdDate >= startDate && cmdDate <= endDate;
      });
    }

    // Filtre par lot technique
    if (filters.lotTechnique) {
      filtered = filtered.filter(commande => commande.lot_technique?.nom === filters.lotTechnique);
    }

    // Filtre par lot géographique
    if (filters.lotGeographique) {
      filtered = filtered.filter(commande => commande.id_lot_departement.toString() === filters.lotGeographique);
    }

    // Filtre par créateur
    if (filters.createur) {
      filtered = filtered.filter(commande => commande.cree_par === filters.createur);
    }

    // Filtre par nom de commande
    if (filters.nomCommande) {
      filtered = filtered.filter(commande =>
        commande.nom && commande.nom.toLowerCase().includes(filters.nomCommande.toLowerCase())
      );
    }

    // Filtre par client
    if (filters.client) {
      filtered = filtered.filter(commande => commande.organisme_nom === filters.client);
    }

    // Filtre par plage de prix
    if (filters.prixRange.min > 0 || filters.prixRange.max < maxPrice) {
      filtered = filtered.filter(commande =>
        commande.prix_total && commande.prix_total >= filters.prixRange.min && commande.prix_total <= filters.prixRange.max
      );
    }

    // Filtre par statut
    if (filters.status) {
      filtered = filtered.filter(commande => commande.status.nom === filters.status);
    }

    // Appliquer le filtre de type (BC/MS)
    if (filters.type && filters.type !== 'Tous') {
      // Ici, on devrait avoir une propriété pour identifier le type dans l'API
      // Pour l'instant, considérons tous comme MS (marché subséquent)
      filtered = filtered.filter(commande => "MS" === filters.type);
    }

    // Mettre à jour les données filtrées
    setFilteredData(filtered);
    setIsFilterModalOpen(false);
  };

  // Récupérer le nombre de commandes avec action requise
  const getActionRequiseCount = () => {
    return commandes.filter(cmd => cmd.status.action === true).length;
  };

  // Créer le tableau des notifications pour TabNav
  const notifications = [
    {
      tabIndex: tabs.indexOf('Action requise'),
      count: getActionRequiseCount()
    }
  ].filter(n => n.tabIndex !== -1 && n.count > 0);

  // Mettre à jour les compteurs de notifications
  useEffect(() => {
    const count = getActionRequiseCount();
    setUnreadCommandes(count);
    setUnreadLocalCommandes(count);
    onNotificationUpdate?.(count);
  }, [commandes, setUnreadCommandes, onNotificationUpdate]);

  // Convertir le statut UI en paramètre de requête API
  const getApiStatusFromUiStatus = (uiStatus: string): string | null => {
    switch (uiStatus) {
      case 'Brouillons':
        return 'status_id=1';
      case 'Accepté':
        return 'status_id=11';
      case 'Refusé':
        return 'status_id=6';
      case 'Expiré':
        return 'status_id=12';
      case 'Action requise':
        return 'status_action=1';
      case 'En attente':
        return 'status_nom="En attente"&&status_action=false';
      default:
        return null;
    }
  };

  // Définition des colonnes du tableau
  const columns = [
    {
      field: 'id',
      header: 'N°',
      renderCell: (value: number) => (
        <div className="date-info text-truncate" title={value.toString()}>
          {value.toString()}
        </div>
      )
    },
    {
      field: 'type',
      header: 'Type',
      renderCell: () => (
        <div className="type-info text-truncate" title="MS">
          MS
        </div>
      )
    },
    {
      field: 'created_at',
      header: 'Date',
      renderCell: (value: string) => {
        const date = new Date(value);
        const formattedDate = date.toLocaleDateString('fr-FR');
        return (
          <div className="date-info text-truncate" title={formattedDate}>
            {formattedDate}
          </div>
        );
      }
    },
    {
      field: 'lot_technique',
      header: 'Lot Technique',
      renderCell: (value: any) => {
        const lotTechniqueName = value?.nom || '-';
        return (
          <div className="lot-technique text-truncate" title={lotTechniqueName}>
            {lotTechniqueName}
          </div>
        );
      }
    },
    {
      field: 'id_lot_departement',
      header: 'Dép',
      renderCell: (value: number) => (
        <div className="lot-geographique text-truncate" title={value.toString()}>
          {value.toString()}
        </div>
      )
    },
    {
      field: 'cree_par',
      header: 'Créé par',
      renderCell: (value: string) => (
        <div className="user-info text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'nom',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="commande-nom text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'organisme_nom',
      header: 'Client',
      renderCell: (value: string) => (
        <div className="client-info text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'prix_total',
      header: 'Total HT',
      renderCell: (value: number | null) => (
        <div className="montant-commande">
          {value !== undefined && value !== null ? `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: (value: { nom: string, id: number, delai: number | null, type_compte: string }) => {
        if (!value) return null;

        // Transforme le nom du statut pour la classe CSS
        const statusClass = value.nom.toLowerCase().replace(/[^a-z0-9]/g, '-');
        // Ajouter la classe "action-requise" si action est vrai
        const actionClass = value.action ? 'action-requise' : '';

        let displayNom = value.nom;
        if ([6, 12].includes(value.id)) {
          displayNom = 'Refusé / Expiré'; // ✅ Fusionné
        }

        return (
          <div className={`status-badge ${statusClass} ${actionClass}`} title={`Type: ${value.type_compte}`}>
            {displayNom}
          </div>
        );
      }
    },
    {
      field: 'expire_at',
      header: 'Temps restant',
      renderCell: (value: string | null, row: Commande) => {
        // Vérifier si expire_at est disponible
        if (row.expire_at) {
          const expireDate = new Date(row.expire_at);
          return <CompteARebourd targetDate={expireDate} />;
        }

        // Fallback sur le délai du statut si expire_at n'est pas disponible
        if (row.status?.delai) {
          const delaiTotal = row.status.delai; // Délai en secondes
          const jours = Math.floor(delaiTotal / 86400);
          const heures = Math.floor((delaiTotal % 86400) / 3600);
          const minutes = Math.floor((delaiTotal % 3600) / 60);
          const secondes = Math.floor(delaiTotal % 60);

          const formattedDelai = `${jours}j ${heures}h ${minutes}m ${secondes}s`;
          return <CompteARebourd targetDate={formattedDelai} />;
        }

        return null;
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: any, row: Commande) => {
        if (!row || !row.status) return null;

        // Récupérer le rôle utilisateur du localStorage
        const userRole = getUserRoleFromLocalStorage();

        // Utiliser le mapping pour déterminer l'action à afficher
        const actionMapping = getStatusActionMapping(row.status.id, userRole);

        // Ne pas afficher de bouton si showButton est false
        if (!actionMapping.showButton) {
          return null;
        }

        return (
          <Button
            value={actionMapping.buttonText}
            variant="secondary"
            size="small"
            onClick={() => {
              // Exécuter l'action en fonction du mapping
              switch (actionMapping.action) {
                case 'reprendre':
                  handleReprendre(row.id.toString());
                  break;
                case 'analyser':
                  handleAnalyser(row.id.toString());
                  break;
                case 'candidater':
                  handleCandidater(row.id.toString());
                  break;
                case 'signer':
                  handleSigner(row.id.toString());
                  break;
                case 'export':
                  handleExport(row.id.toString());
                  break;
                default:
                  // Action par défaut ou pas d'action
                  console.log(`Aucune action définie pour le statut ${row.status.id}`);
              }
            }}
            className={`action-button ${row.status.nom.toLowerCase().replace(/[^a-z0-9]/g, '-')} ${row.status.action ? 'action-requise' : ''}`}
            disabled={actionMapping.disableButton || !row.status.action}
          />
        );
      }
    }
  ];

  // Calcul du prix maximum pour les filtres
  const maxPrice = useMemo(() =>
    Math.max(...commandes.map(cmd => cmd.prix_total || 0))
    , [commandes]);

  // Fonction pour charger les commandes
  const fetchCommandes = async (page = 1, tab = activeTab) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      // Construction des paramètres de requête
      let queryParams = `page=${page}&per_page=50`;

      // Ajout du filtre par statut selon l'onglet actif
      if (tab !== 0 && tab >= 0 && tab < tabs.length) {
        const tabMapping = tabToStatusMapping[tab];
        if (tabMapping && tabMapping.filter) {
          queryParams = `${tabMapping.filter}&page=${page}&per_page=50`;
        }
      }

      // Ajout du filtre par type
      if (selectedType !== 'Tous') {
        queryParams += `&type=${selectedType}`;
      }

      // Effectuer la requête API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/commandes?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const responseData: ApiResponse = await response.json();

      // Vérifier que la réponse a le bon format
      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        throw new Error('Format de réponse API inattendu');
      }

      // Traitement des données avant de les définir dans l'état
      const processedData = responseData.data.map(cmd => ({
        ...cmd,
        // Fournir des valeurs par défaut pour les champs manquants
        nom: cmd.operation?.nom || cmd.nom || `Commande #${cmd.id}`,
        delais: cmd.delais || 0,
        visite: cmd.visite === null ? false : cmd.visite,
        note_financiere: cmd.note_financiere || 0,
        note_total: cmd.note_total || 0,
        // Gérer les relations manquantes
        lot_technique: cmd.lot_technique || { id: 0, nom: 'Non défini' },
        cree_par: cmd.cree_par || 'Système',
        organisme_nom: cmd.operation?.organisme_nom || 'Non défini'
      }));

      setCommandes(processedData);
      setFilteredData(processedData);

      // Mettre à jour la pagination
      setPagination({
        currentPage: responseData.pagination.current_page,
        lastPage: responseData.pagination.last_page,
        perPage: responseData.pagination.per_page,
        total: responseData.pagination.total
      });

      // Mettre à jour les notifications
      const count = responseData.data.filter(cmd => cmd.status.action === true).length;
      setUnreadCommandes(count);
      setUnreadLocalCommandes(count);
      onNotificationUpdate?.(count);

    } catch (err: any) {
      console.error("Erreur de chargement:", err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  };

  // Charger les commandes au montage du composant
  useEffect(() => {
    fetchCommandes(currentPage, activeTab);
  }, []);

  return (
    <div className="commandes-page">
      <section className="tableau-de-bord-section">
        <h1>Toutes les commandes</h1>
        <div className="button-group">
          <div className='button-filter-group'>
            <SearchBar
              onSearch={handleSearch}
              placeholder="Trouver une commande"
              isLoading={searchLoading}
            />
            <Button
              value="exporter"
              variant="third"
              size="small"
              icon={<ExportIcon />}
              onClick={handleExportAll}
              disabled={isExporting}
            />
            <Button
              value="filtrer"
              variant="third"
              size="small"
              icon={<FilterIcon />}
              onClick={handleFilter}
            />
          </div>
          <div className="commandes-actions">
            <Button
              value="Nouveau Bon de Commande"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setIsBonCommandeModalOpen(true)}
            />
            <Button
              value="Nouveau Marché Subséquent"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={handleOpenLancerMarcheModal}
            />
          </div>
        </div>
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          notifications={notifications}
        />
        {loading ? (
          <div className="loading-indicator">
            <p>Chargement des commandes en cours...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>Erreur: {error}</p>
          </div>
        ) : (
          <TableauDeBord
            data={filteredData}
            columns={columns}
            onSearch={handleSearch}
            typeFilters={true}
            onExport={() => handleExportCSV(filteredData, columns, `commandes-${new Date().toISOString().slice(0, 10)}.csv`)}
            onRowClick={handleRowClick}
            isSelectable={true}
            showActionBar={true}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            showActionsColumn={false}
            itemsPerPage={pagination.perPage}
            currentPage={pagination.currentPage}
            totalPages={pagination.lastPage}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
          />
        )}
      </section>
      <LancerMarcheModal
        isOpen={isLancerMarcheModalOpen}
        onClose={() => setIsLancerMarcheModalOpen(false)}
        title="Lancer un marché subséquent"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="MS"
      />

      {/* Pour Bon de Commande (nouveau usage) */}
      <LancerMarcheModal
        isOpen={isBonCommandeModalOpen}
        onClose={() => setIsBonCommandeModalOpen(false)}
        title="Nouveau Bon de Commande"
        showNomOperation={true}
        showLotTechnique={false}
        showLotGeographique={true}
        showDepartement={false}
        mode="BC"
      />
      <FiltreDeCommande
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilterApply}
        data={commandes}
        maxPrice={maxPrice}
      />
    </div>
  );
}

export default Commandes;